import { useContext, useMemo } from "react";
import ReportDowntimeTopFiveBarChart from "components/ReportDowntimeTopFiveBarChart/ReportDowntimeTopFiveBarChart";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useStyles } from "../DowntimeSummaryByFailureType/DowntimeSummaryByFailureType.styles";
import { useParams } from "react-router-dom";
import { DefaultTranslationFieldsInTable, DisplayInOptionsEnum } from "constants/constants";
import { IChartBar } from "components/ReportDowntimeTopFiveBarChart/ReportDowntimeTopFiveBarChartInterfaces";
import NoData from "components/NoData/NoData.view";
import { CircularProgress } from "@mui/material";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";
import { IMillReportCardProps } from "../../../MillDashboardInterfaces";
import { useTranslation } from "react-i18next";
import { translateDefaultEntity } from "utilities/localesUtilities";

const TopFiveFailureCodes = ({ countryCode }: IMillReportCardProps) => {
    const classes = useStyles();
    const { millId } = useParams();
    const { i18n } = useTranslation();
    const { selectedFailureType, selectedFailureCode, handleSetSelectedFailureCode, filters, totalDowntimeSeconds } =
        useContext(SummaryReportDashboardContext);

    const {
        displayUnit: displayIn,
        reportRangeEndDate: endDate,
        reportRangeStartDate: startDate,
    } = useContext(MillDashboardContext);

    const { downtimeSummaryByFailureCodeTotal, loading } =
        MillsEntity.GetDowntimeSummaryTotalsByFailureCodeMillDashboard(
            { ...filters, endDate, startDate, displayIn },
            +millId!,
            Number(selectedFailureType?.id),
        );

    const graphData: IChartBar[] = useMemo(
        () =>
            downtimeSummaryByFailureCodeTotal
                .sort((a, b) =>
                    displayIn === DisplayInOptionsEnum.MINUTES || displayIn === DisplayInOptionsEnum.PERCENT
                        ? b.summary.aggregate.sum.seconds - a.summary.aggregate.sum.seconds
                        : b.summary.aggregate.sum.occurrences - a.summary.aggregate.sum.occurrences,
                )
                .slice(0, 5)
                .reverse()
                .map((failureCode) => {
                    const defaultFailureCodeTranslatedName = translateDefaultEntity(
                        i18n.language,
                        failureCode,
                        "name",
                        DefaultTranslationFieldsInTable.FAILURE_CODE,
                    );
                    const secondsToMinutes =
                        Math.round(failureCode.summary.aggregate.sum.seconds / 60) < 1
                            ? 1
                            : Math.round(failureCode.summary.aggregate.sum.seconds / 60);
                    return {
                        id: defaultFailureCodeTranslatedName,
                        [`${defaultFailureCodeTranslatedName}Color`]: selectedFailureType?.color_code || "",
                        [defaultFailureCodeTranslatedName]:
                            displayIn === DisplayInOptionsEnum.MINUTES
                                ? secondsToMinutes
                                : displayIn === DisplayInOptionsEnum.PERCENT
                                ? totalDowntimeSeconds
                                    ? (100 * failureCode.summary.aggregate.sum.seconds) / totalDowntimeSeconds
                                    : 0
                                : failureCode.summary.aggregate.sum.occurrences,
                        countryCode: countryCode!,
                    };
                }),
        [
            downtimeSummaryByFailureCodeTotal,
            displayIn,
            i18n.language,
            selectedFailureType?.color_code,
            totalDowntimeSeconds,
            countryCode,
        ],
    );

    const selectedFailureCodeName = useMemo(
        () =>
            selectedFailureCode
                ? translateDefaultEntity(
                      i18n.language,
                      selectedFailureCode,
                      "name",
                      DefaultTranslationFieldsInTable.FAILURE_CODE,
                  )
                : undefined,
        [selectedFailureCode, i18n.language],
    );

    return loading ? (
        <div className={classes.noData}>
            <CircularProgress size={24} color="info" />
        </div>
    ) : graphData.length > 0 ? (
        <ReportDowntimeTopFiveBarChart
            data={graphData}
            index="id"
            noPercent={displayIn !== DisplayInOptionsEnum.PERCENT}
            useExternalSelectedBarProp
            selectedBar={selectedFailureCodeName}
            onBarClick={(value) => {
                const failureCodeSelected = downtimeSummaryByFailureCodeTotal.find((failureCode) => {
                    const defaultFailureCodeTranslatedName = translateDefaultEntity(
                        i18n.language,
                        failureCode,
                        "name",
                        DefaultTranslationFieldsInTable.FAILURE_CODE,
                    );
                    return defaultFailureCodeTranslatedName === value;
                });

                if (failureCodeSelected)
                    handleSetSelectedFailureCode({
                        id: failureCodeSelected.failure_code_id,
                        name: failureCodeSelected.name,
                        default_failure_code_translations: failureCodeSelected.default_failure_code_translations,
                    });
            }}
        />
    ) : (
        <NoData className={classes.noData} />
    );
};

export default TopFiveFailureCodes;
